import React from 'react';
import { FiArrowRight, FiZap } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import tiaDesafioSemana from '@/assets/images/tia/TIA-DESAFIO-SEMANA.png';
import tiaPensando from '@/assets/images/tia/TIA-PENSANDO.png';
import tiaUau from '@/assets/images/tia/TIA-UAU.png';
import trophyIcon from '@/assets/images/icons/trophy-3d.png';
import Confetti from '@/components/custom/confetti';
import medalCheck from '@/assets/images/icons/medal-check.svg';
import { TrilhaButton } from './trilha-list-items';

// Unidade de avanço da trilha no modal de fim: fixa avança por seções, personalizada por capítulos.
const TRAIL_UNIT = {
  section: ['seção concluída', 'seções concluídas'],
  chapter: ['capítulo concluído', 'capítulos concluídos']
};

// `level` de GET user_learning_paths/:id/performance → rótulo/cor do badge.
const SUBJECT_LEVEL = {
  good: { label: 'Bom', variant: 'success-soft' },
  regular: { label: 'Médio', variant: 'warning-soft' },
  needs_improvement: { label: 'Ruim', variant: 'info-soft' },
  insufficient_data: { label: 'Sem dados suficientes', variant: 'muted' }
};

// `className`/`headerClassName` deixam um modal sobrescrever largura e altura do topo.
function ChapterDialogShell({ open, onOpenChange, image, imageClassName, className, headerClassName, children }) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        size="sm"
        hideCloseButton
        className={cn('w-[340px] gap-0 overflow-hidden rounded-[20px] border-0 p-0 sm:rounded-[20px]', className)}
      >
        <div className={cn('flex h-[166px] justify-center bg-[#EEF1F6] pt-3', headerClassName)}>
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className={imageClassName}
          />
        </div>
        {/* min-w-0: item de grid não encolhe abaixo do conteúdo — o tópico com truncate esticava o modal */}
        <div className="flex min-w-0 flex-col items-center px-6 pb-6 pt-5 text-center">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

// Fim de capítulo da trilha personalizada. `data`: { chapter, missionsDone, missionsTotal, points }.
// `loading`: aguardando a geração do próximo capítulo / o desempenho.
export function ChapterCompletedDialog({ open, onOpenChange, data, loading = false, onNext }) {
  return (
    <ChapterDialogShell
      open={open}
      onOpenChange={onOpenChange}
      image={tiaDesafioSemana}
      imageClassName="h-full w-auto object-contain object-bottom"
    >
      <img
        src={medalCheck}
        alt=""
        aria-hidden="true"
        className="h-11 w-auto"
      />
      <DialogTitle className="mt-4 text-[22px] font-extrabold leading-tight text-[#222731]">
        CAPÍTULO {data.chapter}
        <span className="block">concluído com sucesso!</span>
      </DialogTitle>
      <DialogDescription asChild>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Badge
            variant="success-soft"
            square
            className="text-xs font-bold"
          >
            {data.missionsDone}/{data.missionsTotal} missões concluídas
          </Badge>
          <Badge
            variant="success-soft"
            square
            className="text-xs font-bold"
          >
            +{data.points} pontos
          </Badge>
        </div>
      </DialogDescription>

      <TrilhaButton
        autoFocus
        disabled={loading}
        onClick={onNext}
        className="group/btn mt-8 h-10 w-auto px-6 py-0"
      >
        Próximo capítulo
        <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
      </TrilhaButton>
    </ChapterDialogShell>
  );
}

// Desempenho do aluno no capítulo concluído (endpoint de performance): a lista são os
// `subjects`, já ordenados pela API (pior → melhor, sem dados por último). A barra é só
// um loader indeterminado ("preparando" o capítulo novo), sem valor.
// `chapter`: capítulo concluído; `nextChapter`: o que acabou de ser gerado.
export function PerformanceDialog({ open, onOpenChange, chapter, nextChapter, subjects = [], onStart }) {
  return (
    <ChapterDialogShell
      open={open}
      onOpenChange={onOpenChange}
      image={tiaPensando}
      imageClassName="h-full w-auto object-contain object-top"
    >
      <DialogTitle className="text-[22px] font-extrabold leading-snug text-[#222731]">
        Preparando o<span className="block">Capítulo {nextChapter}</span>
      </DialogTitle>

      {/* Sem aria-valuenow: progressbar indeterminada */}
      <div
        role="progressbar"
        aria-label={`Preparando o Capítulo ${nextChapter}`}
        className="mt-4 h-2 w-[180px] overflow-hidden rounded-full bg-[#FFF1E6]"
      >
        <div
          className="h-full w-2/5 rounded-full bg-[var(--color-mission,#F99153)]"
          style={{ animation: 'trilha-indeterminate 1.4s ease-in-out infinite' }}
        />
      </div>

      <DialogDescription className="mt-5 text-sm font-semibold leading-snug text-[#222731]">Atualizando conteúdos com base nas suas respostas do Capítulo {chapter}</DialogDescription>

      {subjects.length > 0 && (
        <ul className="mt-4 flex w-full flex-col gap-2">
          {subjects.map(item => {
            const level = SUBJECT_LEVEL[item.level] || SUBJECT_LEVEL.insufficient_data;
            return (
              <li
                key={item.subject}
                className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2.5"
              >
                <span
                  className="min-w-0 truncate text-sm text-neutral-600"
                  title={item.subject}
                >
                  {item.subject}
                </span>
                <Badge
                  variant={level.variant}
                  square
                  className="shrink-0 text-xs font-bold"
                >
                  {level.label}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}

      <TrilhaButton
        autoFocus
        onClick={onStart}
        className="mt-5 h-10 w-auto px-6 py-0"
      >
        <FiZap className="h-4 w-4" />
        Vamos começar!
      </TrilhaButton>
    </ChapterDialogShell>
  );
}

// Fim de trilha: mesma chuva de confete do modal de conquista (PtsModal).
// `data`: { trailName, unit: 'section' | 'chapter', unitsDone, steps, points }; badge com valor null não aparece.
export function TrailCompletedDialog({ open, onOpenChange, data, onClose }) {
  const [unitOne, unitMany] = TRAIL_UNIT[data.unit];

  return (
    <>
      {/* Confete atrás do modal (z-index padrão do canvas-confetti, abaixo do Dialog) */}
      {open && <Confetti />}
      <ChapterDialogShell
        open={open}
        onOpenChange={onOpenChange}
        image={tiaUau}
        className="w-[400px]"
        headerClassName="h-[190px]"
        imageClassName="h-full w-auto object-contain object-top"
      >
        <img
          src={trophyIcon}
          alt=""
          aria-hidden="true"
          className="h-14 w-auto"
        />
        <DialogTitle className="mt-3 text-lg font-extrabold leading-snug text-[#222731]">Uau! Você venceu a trilha {data.trailName}!</DialogTitle>
        <DialogDescription asChild>
          <div className="mt-2 flex flex-wrap justify-center gap-1.5">
            {data.unitsDone != null && (
              <Badge
                variant="amber-soft"
                square
                className="text-[11px] font-semibold"
              >
                {data.unitsDone} {data.unitsDone === 1 ? unitOne : unitMany}
              </Badge>
            )}
            {data.steps != null && (
              <Badge
                variant="amber-soft"
                square
                className="text-[11px] font-semibold"
              >
                {data.steps} passos dados
              </Badge>
            )}
            {data.points != null && (
              <Badge
                variant="success-soft"
                square
                className="text-[11px] font-semibold"
              >
                +{data.points} pts
              </Badge>
            )}
          </div>
        </DialogDescription>

        <TrilhaButton
          autoFocus
          onClick={onClose}
          className="mt-6 h-9 w-auto px-6 py-0"
        >
          Valeu!
        </TrilhaButton>
      </ChapterDialogShell>
    </>
  );
}
